import React, { useEffect, useState } from 'react';

import http from '../../../api/http';
import PageHero from '../../../components/page/PageHero';
import StatusBadge from '../../../components/ui/StatusBadge';

const DashboardWidget = ({ widget }) => {
  const data = widget.data;
  return (
    <section className="card stack" aria-labelledby={`dashboard-widget-${widget.id}`}>
      <h2 className="section-title" id={`dashboard-widget-${widget.id}`}>{widget.title}</h2>
      {widget.type === 'event-feed' ? (
        Array.isArray(data) && data.length ? (
          <ul className="dashboard-event-list">
            {data.map((event) => (
              <li key={event.id}>
                <strong>{event.section}</strong> {event.action} <span>#{event.recordId}</span>
                <small className="small-muted"> {event.occurredAt ? new Date(event.occurredAt).toLocaleString() : ''}</small>
              </li>
            ))}
          </ul>
        ) : <p className="small-muted">No recent changes.</p>
      ) : null}
      {widget.type === 'health-badge' ? (
        data ? (
          <div className="row-between">
            <StatusBadge value={data.health?.status} />
            <strong>{data.health?.score === null ? 'Insufficient evidence' : `${data.health?.score}/100`}</strong>
          </div>
        ) : <p className="small-muted">The selected record was not found.</p>
      ) : null}
      {widget.type === 'report-metric' ? (
        <div className="stack">
          <span className="hero-stat-label">{data?.label || data?.field}</span>
          <strong className="dashboard-metric">{data?.value ?? '—'}</strong>
        </div>
      ) : null}
      {widget.type === 'report-table' ? (
        data?.rows?.length ? (
          <div className="table-scroll">
            <table className="data-table">
              <thead><tr>{data.columns.map((column) => <th key={column.key}>{column.label}</th>)}</tr></thead>
              <tbody>
                {data.rows.map((row, index) => (
                  <tr key={row.id ?? index}>
                    {data.columns.map((column) => <td key={column.key}>{String(row[column.key] ?? '')}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p className="small-muted">This report has no rows.</p>
      ) : null}
    </section>
  );
};

const DashboardsPage = () => {
  const [dashboards, setDashboards] = useState([]);
  const [slug, setSlug] = useState('');
  const [snapshot, setSnapshot] = useState(null);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    http.get('/dashboards')
      .then(({ data }) => {
        const rows = Array.isArray(data?.data) ? data.data : [];
        if (active) {
          setDashboards(rows);
          setSlug(rows[0]?.slug || '');
        }
      })
      .catch((requestError) => {
        if (active) setError(requestError?.response?.data?.message || requestError.message || 'Unable to load dashboards.');
      })
      .finally(() => {
        if (active) setLoadingList(false);
      });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!slug) {
      setSnapshot(null);
      return undefined;
    }
    let active = true;
    setLoadingData(true);
    setError('');
    http.get(`/dashboards/${encodeURIComponent(slug)}/data`)
      .then(({ data }) => { if (active) setSnapshot(data); })
      .catch((requestError) => {
        if (active) {
          setSnapshot(null);
          setError(requestError?.response?.data?.message || requestError.message || 'Unable to load dashboard data.');
        }
      })
      .finally(() => { if (active) setLoadingData(false); });
    return () => { active = false; };
  }, [slug]);

  const selected = dashboards.find((dashboard) => dashboard.slug === slug);

  return (
    <div className="stack">
      <PageHero
        title={snapshot?.dashboard?.title || 'Dashboards'}
        subtitle={snapshot?.dashboard?.description || 'Composable views of reports, health and recent workspace activity.'}
        actions={dashboards.length > 1 ? (
          <label>
            Dashboard
            <select aria-label="Dashboard" value={slug} onChange={(event) => setSlug(event.target.value)}>
              {dashboards.map((dashboard) => <option key={dashboard.slug} value={dashboard.slug}>{dashboard.name}</option>)}
            </select>
          </label>
        ) : null}
      />
      {loadingList || loadingData ? <p>Loading dashboard...</p> : null}
      {error ? <p className="error-text" role="alert">{error}</p> : null}
      {!loadingList && !dashboards.length && !error ? <p>No active dashboards are configured yet.</p> : null}
      {snapshot?.widgets?.length ? (
        <div className="stack dashboard-widget-list">
          {snapshot.widgets.map((widget) => <DashboardWidget key={widget.id} widget={widget} />)}
        </div>
      ) : null}
      {selected && !snapshot && !loadingData && !error ? <p>Dashboard data is not available.</p> : null}
    </div>
  );
};

export default DashboardsPage;
